import Tooltip from '@betterfunnel/ui-core/Tooltip';
import { CustomAnalyticsEvent, ExternalPixelEvent } from '@betterfunnel/types';
import { ReactNode, useCallback } from 'react';
import {
  AnalyticsAttributes,
  AnalyticsAttributesWithCustomEvents,
  capitalize,
  classNames,
  getAnalyticsAttributes,
  getAnalyticsAttributesWithEvents,
} from '../../utils';
import { AnalyticsTypeOption } from '../AnalyticsTypeButton';

const StatConfig = {
  device: [
    {
      key: 'Mobile',
      color: 'text-blue-400',
    },
    {
      key: 'Desktop',
      color: 'text-green-400',
    },
  ],
};

interface BaseProps {
  analyticsType: AnalyticsTypeOption | null;
  analyticsKey: string;
  analyticsData: Record<string, any>;
  showAll: boolean;
  fill?: boolean;
}

interface SimpleProps extends BaseProps {
  analyticsCustomEvents?: undefined;
  analyticsCustomEventsData?: undefined;
  format: (attrs: AnalyticsAttributes) => ReactNode;
}

interface PropsWithExternalCustomEvents extends BaseProps {
  analyticsCustomEventsData: Record<string, any>;
  analyticsCustomEvents: (ExternalPixelEvent | CustomAnalyticsEvent)[];
  format: (attrs: AnalyticsAttributesWithCustomEvents) => ReactNode;
}

type Props = SimpleProps | PropsWithExternalCustomEvents;

export const StatCellValues = ({
  analyticsType,
  analyticsKey,
  analyticsData,
  format,
  showAll,
  fill,
  analyticsCustomEvents = undefined,
  analyticsCustomEventsData = undefined,
}: Props) => {
  const getAttrs = useCallback(
    (
      analyticsData: Record<string, any>,
      analyticsCustomEvents?: (ExternalPixelEvent | CustomAnalyticsEvent)[],
    ): AnalyticsAttributes => {
      if (analyticsCustomEvents) {
        return getAnalyticsAttributesWithEvents(
          analyticsKey,
          analyticsData,
          analyticsCustomEvents,
        );
      } else {
        return getAnalyticsAttributes(analyticsKey, analyticsData);
      }
    },
    [analyticsKey],
  );

  if (!analyticsData) {
    return (
      <div className="h-2.5 w-1/2 min-w-7 max-w-12 animate-pulse rounded-md bg-gray-300"></div>
    );
  }
  if (!analyticsType) {
    const normal = getAttrs(analyticsData);
    const customEvents = analyticsCustomEventsData
      ? getAttrs(analyticsCustomEventsData, analyticsCustomEvents)
      : {};
    return format({
      ...customEvents,
      ...normal,
    });
  }

  const statConfig = StatConfig[analyticsType];

  const analyticsTypeValues = Object.fromEntries([
    ...Object.entries(
      analyticsCustomEventsData?.[analyticsType || ''] ?? [],
    ).map(([k, v]) => [k, getAttrs(v as any, analyticsCustomEvents)]),

    ...Object.entries(analyticsData?.[analyticsType || ''] ?? []).map(
      ([k, v]) => [k, getAttrs(v as any)],
    ),
  ]);

  const knownStats = new Set(statConfig.map(({ key }) => key));
  const entries = statConfig
    .map(
      ({ key }) =>
        [key, analyticsTypeValues[key] || {}] satisfies [
          string,
          AnalyticsAttributesWithCustomEvents,
        ],
    )
    .concat(
      Object.entries(analyticsTypeValues)
        .filter(([k]) => !knownStats.has(k))
        .sort(([a], [b]) => a.localeCompare(b)),
    );
  if (showAll) {
    entries.push(['all', getAttrs(analyticsData)]);
  }
  return (
    <div className="flex items-center gap-4 text-center text-gray-900">
      {entries.map(([k, v], i) => (
        <>
          <Tooltip
            key={k}
            text={k === 'undefined' ? 'Unknown' : capitalize(k)}
            tooltipClassName={classNames(
              statConfig.find((s) => s.key === k)?.color ?? 'text-orange-400',
              k === 'all' && 'text-white',
              fill && 'flex-1 justify-center',
            )}
          >
            {format(v)}
          </Tooltip>
          {i < entries.length - 1 ? (
            <div key={`divider-${k}`} className="b h-4 shrink-0 border" />
          ) : (
            <></>
          )}
        </>
      ))}
    </div>
  );
};
